import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { FaBars, FaTimes, FaUser, FaMotorcycle, FaUsers, FaCog, FaBell } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();

  const handleLogout = () => {
    setIsOpen(false);
    logout();
    navigate('/');
  };

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="flex justify-between items-center py-3 sm:py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <FaMotorcycle className="text-blue-600 text-xl sm:text-2xl" />
            <span className="text-lg sm:text-xl font-bold text-gray-800">SastaMastaRide</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {user?.role !== 'admin' && (
              <>
                <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors">
                  Home
                </Link>
              </>
            )}
            
            {isAuthenticated ? (
              <>
                {user.role === 'rider' && (
                  <>
                    <Link to="/create-trip" className="text-gray-700 hover:text-blue-600 transition-colors">
                      Offer Ride
                    </Link>
                    <Link to="/my-trips" className="relative text-gray-700 hover:text-blue-600 transition-colors">
                      My Trips
                      {unreadCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Link>
                  </>
                )}
                {user.role === 'passenger' && (
                  <Link to="/my-bookings" className="text-gray-700 hover:text-blue-600 transition-colors">
                    My Bookings
                  </Link>
                )}
                {user.role === 'admin' && (
                  <>
                    <Link to="/admin" className="text-gray-700 hover:text-blue-600 transition-colors">
                      Dashboard
                    </Link>
                    <Link to="/admin/users" className="text-gray-700 hover:text-blue-600 transition-colors">
                      Users
                    </Link>
                    <Link to="/admin/trips" className="text-gray-700 hover:text-blue-600 transition-colors">
                      Trips
                    </Link>
                    <Link to="/admin/bookings" className="text-gray-700 hover:text-blue-600 transition-colors">
                      Bookings
                    </Link>
                    <Link to="/admin/analytics" className="text-gray-700 hover:text-blue-600 transition-colors">
                      Analytics
                    </Link>
                    <Link to="/admin/payouts" className="text-gray-700 hover:text-blue-600 transition-colors">
                      Payouts
                    </Link>
                  </>
                )}
                
                <div className="relative group">
                  <button className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors">
                    <FaUser />
                    <span>{user.name}</span>
                  </button>
                  
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <Link to="/profile" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <FaCog className="mr-2" />
                      Profile
                    </Link>
                    <button 
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="text-gray-700 hover:text-blue-600 transition-colors">
                  Login
                </Link>
                <Link to="/register" className="btn-primary">
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-blue-600 transition-colors"
            >
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 max-h-[calc(100vh-80px)] overflow-y-auto">
            <div className="flex flex-col space-y-3 sm:space-y-4">
              {user?.role !== 'admin' && (
                <>
                  <Link to="/" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                    Home
                  </Link>
                </>
              )}
              
              {isAuthenticated ? (
                <>
                  {user.role === 'rider' && (
                    <>
                      <Link to="/create-trip" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                        Offer Ride
                      </Link>
                      <Link to="/my-trips" onClick={handleLinkClick} className="relative text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                        My Trips
                        {unreadCount > 0 && (
                          <span className="ml-1 inline-block bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </Link>
                    </>
                  )}
                  {user.role === 'passenger' && (
                    <Link to="/my-bookings" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                      My Bookings
                    </Link>
                  )}
                  {user.role === 'admin' && (
                    <>
                      <Link to="/admin" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                        Dashboard
                      </Link>
                      <Link to="/admin/users" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                        Users
                      </Link>
                      <Link to="/admin/trips" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                        Trips
                      </Link>
                      <Link to="/admin/bookings" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                        Bookings
                      </Link>
                      <Link to="/admin/analytics" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                        Analytics
                      </Link>
                      <Link to="/admin/payouts" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                        Payouts
                      </Link>
                    </>
                  )}
                  
                  <Link to="/profile" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                    Profile
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="text-left text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={handleLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors py-2 min-h-[44px] flex items-center">
                    Login
                  </Link>
                  <Link to="/register" onClick={handleLinkClick} className="btn-primary min-h-[44px] flex items-center justify-center">
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
